import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from '../api';
import { getAvatarUrl } from '../utils/media';
import { useAvatarAuth } from '../hooks/useAvatarAuth';

export default function EditProfile() {
  const { user } = useSelector((state) => state.auth);
  const avatarAuth = useAvatarAuth();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({ username: '', bio: '' });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [currentAvatar, setCurrentAvatar] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  const fetchProfile = async () => {
    try {
      const { data } = await api.get('/creator/profile');
      setForm({
        username: data.username || '',
        bio: data.bio || '',
      });
      setCurrentAvatar(data.avatar_url || '');
      // If no username, show form directly
      setEditing(!data.username);
    } catch (err) {
      setError('Error loading profile');
      setEditing(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be under 2MB');
      return;
    }

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('username', form.username);
      formData.append('bio', form.bio);
      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      const { data } = await api.put('/creator/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setForm({ username: data.username || '', bio: data.bio || '' });
      setCurrentAvatar(data.avatar_url || '');
      setAvatarFile(null);
      setAvatarPreview(null);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading profile...</div>;

  const displayAvatar = avatarPreview || getAvatarUrl(currentAvatar, avatarAuth);
  const initial = (user?.displayName || user?.email || '?')[0].toUpperCase();

  // Saved profile view
  if (!editing) {
    return (
      <div className="edit-profile-page">
        <h1>My Profile</h1>
        <div className="edit-profile-view">
          <div className="edit-profile-avatar-section">
            <div className="edit-profile-avatar">
              {displayAvatar ? (
                <img src={displayAvatar} alt="Avatar" />
              ) : (
                <span>{initial}</span>
              )}
            </div>
          </div>
          <div className="edit-profile-field">
            <span className="edit-profile-field-label">Username</span>
            <span className="edit-profile-field-value">@{form.username}</span>
          </div>
          <div className="edit-profile-field">
            <span className="edit-profile-field-label">Email</span>
            <span className="edit-profile-field-value">{user?.email}</span>
          </div>
          {form.bio && (
            <div className="edit-profile-field">
              <span className="edit-profile-field-label">Bio</span>
              <span className="edit-profile-field-value">{form.bio}</span>
            </div>
          )}
          <button className="btn btn-primary" onClick={() => setEditing(true)}>
            Edit Profile
          </button>
        </div>
      </div>
    );
  }

  // Edit view
  return (
    <div className="edit-profile-page">
      <h1>Edit Profile</h1>

      <form className="edit-profile-form" onSubmit={handleSubmit}>
        <div className="edit-profile-avatar-section">
          <div className="edit-profile-avatar" onClick={() => fileInputRef.current?.click()}>
            {displayAvatar ? (
              <img src={displayAvatar} alt="Avatar" />
            ) : (
              <span>{initial}</span>
            )}
            <div className="edit-profile-avatar-overlay">Change</div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleAvatarChange}
            style={{ display: 'none' }}
          />
          <p className="edit-profile-avatar-hint">JPG, PNG or WebP. Max 2MB.</p>
        </div>

        <label className="edit-profile-label">
          Username
          <input
            type="text"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder="my_username"
            maxLength={30}
            pattern="[a-zA-Z0-9_]{3,30}"
            title="3-30 alphanumeric characters or underscore"
            required
          />
        </label>

        <label className="edit-profile-label">
          Email
          <input type="email" value={user?.email || ''} disabled />
        </label>

        <label className="edit-profile-label">
          Bio / Description
          <textarea
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            placeholder="Tell the world about your content..."
            rows={4}
            maxLength={500}
          />
        </label>

        {error && <div className="error-msg">{error}</div>}

        <div className="edit-profile-buttons">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          {form.username && (
            <button type="button" className="btn btn-outline" onClick={() => { setEditing(false); setAvatarFile(null); setAvatarPreview(null); setError(null); }}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
